import React, { useState } from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Upload, Trash2, FileText, Loader2, Circle } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Onboarding checklist page.
 * Used by both admin (via /admin/drivers/:id/onboarding) and driver
 * (via /driver/onboarding — read-own). Uploading files with a click,
 * ticking non-file items with the check button.
 */
const Onboarding = ({ driverIdProp, editableTicks = true }) => {
  const params = useParams();
  const driverId = driverIdProp || params.id || params.driverId;
  const { data, mutate, isLoading } = useSWR(driverId ? `/onboarding/${driverId}` : null, fetcher);
  const [uploading, setUploading] = useState(null);

  if (isLoading || !data) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const upload = async (item, file) => {
    setUploading(item);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/onboarding/${driverId}/upload?item=${item}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      mutate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setUploading(null);
  };

  const tick = async (item, done) => {
    try {
      await api.post(`/onboarding/${driverId}/tick`, { item, done });
      mutate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Tick failed"); }
  };

  const remove = async (item) => {
    if (!window.confirm("Remove this item?")) return;
    try {
      await api.delete(`/onboarding/${driverId}/${item}`);
      toast.success("Removed");
      mutate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-3xl mx-auto pb-24">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Onboarding</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mt-1">
            {data.driver_name || "Driver"} onboarding
          </h1>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Progress</div>
          <div className="font-heading text-2xl font-bold text-brand-red">{data.percent}%</div>
          <div className="text-xs text-slate-500">{data.completed} / {data.total} done</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-brand-red transition-all" style={{ width: `${data.percent}%` }} />
      </div>

      <Card className="p-0 rounded-2xl border-brand-border overflow-hidden divide-y divide-brand-border">
        {data.items.map((it) => {
          const done = it.status === "done";
          return (
            <div key={it.key} className="p-3 flex items-center gap-3" data-testid={`onb-${it.key}`}>
              <div className="w-9 h-9 rounded-lg grid place-items-center bg-slate-50">
                {done ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                ) : (
                  <Circle className="w-5 h-5 text-slate-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{it.label}
                  {it.required && !done && <span className="ml-1 text-[10px] text-red-500">required</span>}
                </div>
                {it.file_name && (
                  <div className="text-[11px] text-slate-500 truncate">
                    <FileText className="w-3 h-3 inline mr-0.5" /> {it.file_name}
                  </div>
                )}
                {!it.needs_file && it.ticked_by && (
                  <div className="text-[11px] text-slate-500">Ticked by {it.ticked_by}</div>
                )}
              </div>
              {it.needs_file ? (
                <div className="flex items-center gap-1.5">
                  {done && (
                    <a href={`${API}/api/onboarding/${driverId}/file/${it.key}`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="ghost" data-testid={`onb-view-${it.key}`}>
                        <FileText className="w-3.5 h-3.5" />
                      </Button>
                    </a>
                  )}
                  <label className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 text-xs cursor-pointer hover:bg-slate-50">
                    {uploading === it.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                    {done ? "Replace" : "Upload"}
                    <input type="file" hidden accept=".pdf,image/*" data-testid={`onb-upload-${it.key}`}
                            onChange={(e) => e.target.files?.[0] && upload(it.key, e.target.files[0])} />
                  </label>
                  {done && (
                    <Button size="sm" variant="ghost" onClick={() => remove(it.key)}
                            data-testid={`onb-del-${it.key}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </Button>
                  )}
                </div>
              ) : (
                editableTicks && (
                  <Button size="sm" variant={done ? "outline" : "default"}
                          className={done ? "" : "bg-emerald-600 hover:bg-emerald-700"}
                          onClick={() => tick(it.key, !done)}
                          data-testid={`onb-tick-${it.key}`}>
                    {done ? "Undo" : "Mark done"}
                  </Button>
                )
              )}
            </div>
          );
        })}
      </Card>
    </div>
  );
};

export default Onboarding;
